import { type MouseEvent, use } from 'react'
import { useTranslation } from 'react-i18next'

import { Text, type TextProps } from '@chakra-ui/react'
import type { LatLngExpression } from 'leaflet'

import { MapSelectedContext } from './MapSelectedContext'

interface ClickableLocationProps extends TextProps {
	ref?: React.RefObject<HTMLParagraphElement | null>
	id: string
	location: string
	coordinates: LatLngExpression
	comparisonMode?: boolean
	isTrimmed?: boolean
}

export const ClickableLocation = ({
	ref,
	id,
	location,
	coordinates,
	onClick,
	comparisonMode = false,
	isTrimmed,
	...props
}: ClickableLocationProps) => {
	const [, setSelected] = use(MapSelectedContext)
	const { t } = useTranslation('tooltips')

	const title = t('labelOpenLocationMap', { location })
	const clickHandler = (e: MouseEvent<HTMLParagraphElement>) => {
		setSelected(coordinates ? { coordinates, id } : null)
		onClick?.(e)
	}

	if (comparisonMode) {
		const { noOfLines } = props

		return (
			<Text
				// We can't just replace it with a Button because of style incompatibilities
				as="button"
				cursor="pointer"
				onClick={clickHandler}
				// Fix for GA4
				sx={{
					'*': {
						pointerEvents: 'none'
					}
				}}
				{...props}
			>
				<Text
					ref={ref}
					noOfLines={noOfLines}
				>
					{location}
				</Text>
			</Text>
		)
	}

	return (
		<Text
			// We can't just replace it with a Button because of style incompatibilities
			as="button"
			title={title}
			onClick={clickHandler}
			ref={ref}
			// Fix for GA4
			sx={{
				'*': {
					pointerEvents: 'none'
				}
			}}
			{...props}
		>
			<Text noOfLines={isTrimmed ? 1 : undefined}>{location}</Text>
		</Text>
	)
}

ClickableLocation.displayName = 'ClickableLocation'
